import React, { useEffect, useMemo, useRef, useState } from "react";

const SimplexAnimation = ({ isActive, size = 320, className = "" }) => {
  const viewBox = "0 0 100 100";

  const points = useMemo(
    () => [
      { x: 5, y: 95 },  // start
      { x: 5, y: 40 },  // up
      { x: 31, y: 40 }, // right
      { x: 60, y: 64 }, // optimal
    ],
    []
  );

  const segments = useMemo(() => {
    const dist = (a, b) => Math.hypot(b.x - a.x, b.y - a.y);
    const segs = [];
    for (let i = 0; i < points.length - 1; i++) {
      const a = points[i];
      const b = points[i + 1];
      segs.push({
        d: `M ${a.x} ${a.y} L ${b.x} ${b.y}`,
        len: dist(a, b),
      });
    }
    return segs;
  }, [points]);

  const [segIndex, setSegIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  // Timing
  const SEGMENT_DURATION_MS = 800;
  const BETWEEN_SEGMENTS_PAUSE = 250;
  const FINAL_HOLD_MS = 700;

  const rafRef = useRef(null);
  const t0Ref = useRef(0);
  const pausingRef = useRef(false);
  const pausedAtRef = useRef(0);
  const finalHoldRef = useRef(false);

  useEffect(() => {
    if (!isActive || segments.length === 0) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      t0Ref.current = 0;
      pausingRef.current = false;
      finalHoldRef.current = false;
      setSegIndex(0);
      setProgress(0);
      return;
    }

    const step = (t) => {
      if (t0Ref.current === 0) t0Ref.current = t;

      
      if (pausingRef.current) {
        const pauseDur = finalHoldRef.current ? FINAL_HOLD_MS : BETWEEN_SEGMENTS_PAUSE;

        if (t - pausedAtRef.current >= pauseDur) {
          pausingRef.current = false;
          t0Ref.current = t;

          
          if (finalHoldRef.current) {
            finalHoldRef.current = false;
            setSegIndex(0);
            setProgress(0);
          }
        }

        rafRef.current = requestAnimationFrame(step);
        return;
      }

      // Normal segment drawing
      const elapsed = t - t0Ref.current;
      const p = Math.min(1, elapsed / SEGMENT_DURATION_MS);
      setProgress(p);

      if (p >= 1) {
        const last = segments.length - 1;

        if (segIndex < last) {
          // next segment
          setSegIndex((i) => i + 1);
          setProgress(0);
          pausingRef.current = true;
          pausedAtRef.current = t;
          t0Ref.current = t;
        } else {
          
          pausingRef.current = true;
          pausedAtRef.current = t;
          finalHoldRef.current = true;
          t0Ref.current = t;
        }
      }

      rafRef.current = requestAnimationFrame(step);
    };

    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      t0Ref.current = 0;
      pausingRef.current = false;
      finalHoldRef.current = false;
    };
  }, [isActive, segments.length, segIndex]);

  const stroke = "#2563eb";

  // Thinner
  const AXIS_W = 4;
  const PATH_W = 4.5;

  const maxWidth =
    size ? (typeof size === "number" ? `${size}px` : size) : undefined;

  return (
    <div
      className={`relative overflow-hidden rounded-lg bg-[#ef4444] aspect-square w-full${
        className ? ` ${className}` : ""
      }`}
      style={{ maxWidth, aspectRatio: "1 / 1" }}
    >
      <svg
        viewBox={viewBox}
        preserveAspectRatio="xMidYMid meet"
        xmlns="http://www.w3.org/2000/svg"
        className="h-full w-full"
      >
        <defs>
          <pattern
            id="feasibleDash"
            patternUnits="userSpaceOnUse"
            width="8"
            height="8"
          >
            <line
              x1="0"
              y1="8"
              x2="8"
              y2="0"
              stroke="rgba(255,255,255,0.7)"
              strokeWidth="2"
            />
          </pattern>
        </defs>

        
        <g transform="translate(4, 0)">
          
          <line x1="5" y1="10" x2="5" y2="95" stroke="#f3f4f6" strokeWidth={AXIS_W} />
          <line x1="3" y1="95" x2="90" y2="95" stroke="#f3f4f6" strokeWidth={AXIS_W} />
          <line x1="5" y1="40" x2="90" y2="40" stroke="#f3f4f6" strokeWidth={3} />
          <line x1="60" y1="95" x2="60" y2="5" stroke="#f3f4f6" strokeWidth={3} />
          <line x1="0" y1="15" x2="90" y2="90" stroke="#f3f4f6" strokeWidth={3} />

          
          <polygon
            points="5,40 5,95 60,95 60,65 30,40"
            fill="url(#feasibleDash)"
            stroke="#f3f4f6"
            strokeWidth={3}
          />

          
          {segments.map((s, i) =>
            i < segIndex ? (
              <path
                key={`done-${i}`}
                d={s.d}
                fill="none"
                stroke={stroke}
                strokeWidth={PATH_W}
                strokeLinecap="round"
              />
            ) : null
          )}

          
          {segments[segIndex] && (
            <AnimatedSegment
              d={segments[segIndex].d}
              length={segments[segIndex].len}
              progress={progress}
              stroke={stroke}
              strokeWidth={PATH_W}
            />
          )}

          
          <circle cx={points[0].x} cy={points[0].y} r="3.0" fill={stroke} />
          <circle cx={points[1].x} cy={points[1].y} r="3.0" fill={stroke} />
          <circle cx={points[2].x} cy={points[2].y} r="3.0" fill={stroke} />
          <circle cx={points[3].x} cy={points[3].y} r="4.0" fill={stroke} />
        </g>
      </svg>
    </div>
  );
};

const AnimatedSegment = ({ d, length, progress, stroke, strokeWidth }) => {
  const dashArray = length;
  const dashOffset = (1 - progress) * length;

  return (
    <path
      d={d}
      fill="none"
      stroke={stroke}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeDasharray={dashArray}
      strokeDashoffset={dashOffset}
    />
  );
};

export default SimplexAnimation;
